<template>
  <div class="area-envios p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-paper-plane"></i>
      Envio de Documentos
    </h5>

    <!-- Alerta Importante -->
    <div class="alert-importante mb-4">
      <div class="alert-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <div class="alert-content">
        <div class="alert-title">
          <strong>ATENÇÃO - Leia com Cuidado</strong>
        </div>
        <p class="alert-text">
          Após realizar o upload dos arquivos, o candidato deverá abrir individualmente cada documento para conferência e verificação da integridade do arquivo. Em caso de qualquer irregularidade, como arquivos corrompidos e ilegíveis, o candidato deverá excluir o arquivo e fazer novo envio. Caso haja dificuldade técnica e necessite de suporte, poderá entrar em contato através de um dos canais de suporte disponibilizados pela Banca, observando os prazos e os horários de expediente:
        </p>
        <p class="alert-text mb-0">
          através do e-mail <a href="mailto:suporte@ibcgp.org.br">suporte@ibcgp.org.br</a>; 
          ou por mensagem de texto através do whatsapp pelo telefone (67) 3042-4800.
        </p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loadingInscricoes" class="loading-container">
      <div class="spinner-wrapper">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="loading-text mt-3">Carregando inscrições disponíveis...</p>
      </div>
    </div>

    <div v-else>
      <!-- Inscrições Disponíveis para Envio -->
        <div class="section-header mb-3">
        <i class="fas fa-clipboard-list"></i>
        <span>Inscrições Disponíveis para Envio de Documentos</span>
        </div>

      <div v-if="!inscricoesAtivas.length" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-clipboard-list"></i>
              </div>
        <h6 class="empty-title">Nenhuma inscrição disponível</h6>
        <p class="empty-text">Você não possui inscrições ativas para envio de documentos.</p>
        <small class="empty-hint">Somente inscrições ativas podem enviar documentos.</small>
            </div>

      <template v-else>
        <!-- Desktop Table View -->
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th><i class="fas fa-building"></i> Concurso</th>
                <th><i class="fas fa-briefcase"></i> Vaga</th>
                <th><i class="fas fa-calendar-times"></i> Prazo</th>
                <th><i class="fas fa-cogs"></i> Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inscricao in inscricoesAtivas" :key="inscricao.id">
                <td>
                  <strong>{{ inscricao.concurso?.titulo || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ inscricao.concurso?.prefeitura || 'N/A' }}</small>
                </td>
                <td>{{ inscricao.vaga?.cargo || 'N/A' }}</td>
                <td>
                  <span :class="getClassePrazo(inscricao)">
                    {{ prazosDisponiveis(inscricao) }}
                  </span>
                </td>
                <td>
                  <button 
                    class="btn btn-sm btn-primary" 
                    @click="abrirModal(inscricao)"
                    :disabled="!envioAberto">
                    <i class="fas fa-upload"></i>
                    {{ envioAberto ? 'Enviar Documento' : 'Sem Prazo Aberto' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards d-md-none">
          <div 
            v-for="inscricao in inscricoesAtivas" 
            :key="inscricao.id" 
            class="mobile-card"
          >
            <div class="card-header-mobile">
              <i class="fas fa-building"></i>
              <strong>{{ inscricao.concurso?.titulo || 'N/A' }}</strong>
            </div>
            <div class="card-body-mobile">
              <div class="card-item">
                <i class="fas fa-map-marker-alt"></i>
                <span class="label">Prefeitura:</span>
                <span class="value">{{ inscricao.concurso?.prefeitura || 'N/A' }}</span>
              </div>
              <div class="card-item">
                <i class="fas fa-briefcase"></i>
                <span class="label">Vaga:</span>
                <span class="value">{{ inscricao.vaga?.cargo || 'N/A' }}</span>
              </div>
              <div class="card-item">
              <i class="fas fa-calendar-times"></i>
              <span class="label">Prazo:</span>
              <span :class="getClassePrazo(inscricao)" class="value">
                {{ prazosDisponiveis(inscricao) }}
              </span>
            </div>
            </div>
            <div class="card-footer-mobile">
              <button 
                class="btn btn-primary btn-block-mobile" 
                @click="abrirModal(inscricao)"
                :disabled="!envioAberto">
                <i class="fas fa-upload"></i>
                {{ envioAberto ? 'Enviar Documento' : 'Sem Prazo Aberto' }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- Meus Envios -->
      <div class="section-header mb-3 mt-5">
        <i class="fas fa-list-alt"></i>
        <span>Meus Envios de Documentos</span>
            </div>

      <!-- Loading Documentos -->
      <div v-if="loadingDocumentos" class="loading-container">
        <div class="spinner-wrapper">
          <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">Carregando...</span>
            </div>
          <p class="loading-text mt-3">Carregando documentos enviados...</p>
            </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!list.length" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-file-upload"></i>
      </div>
        <h6 class="empty-title">Nenhum documento enviado</h6>
        <p class="empty-text">Você ainda não realizou nenhum envio de documentos.</p>
        <small class="empty-hint">Use o formulário acima para enviar seus documentos.</small>
      </div>

      <!-- Tabela de Documentos - Desktop -->
      <template v-else>
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th><i class="fas fa-calendar"></i> Data de Envio</th>
                <th><i class="fas fa-building"></i> Concurso</th>
                <th><i class="fas fa-file-pdf"></i> Documento</th>
                <th><i class="fas fa-paperclip"></i> Anexo</th>
                <th><i class="fas fa-info-circle"></i> Status</th>
                <th class="text-center"><i class="fas fa-cogs"></i> Ações</th>
              </tr>
            </thead>
            <tbody>
            <template v-for="grupo in enviosAgrupadosPorInscricao">

              <tr class="table-group-header" :key="`grp-${grupo.key}`">
                <td colspan="6">
                  <i class="fas fa-folder-open"></i>
                  <strong>{{ grupo.inscricao?.concurso?.prefeitura || grupo.inscricao?.concurso?.titulo || 'Concurso' }}</strong>
                  <span class="text-muted"> — {{ grupo.inscricao?.vaga?.cargo || 'Cargo' }}</span>
                  <span class="text-muted" v-if="grupo.inscricao?.id"> (Inscrição #{{ grupo.inscricao.id }})</span>
                  <span class="text-muted"> • {{ grupo.itens.length }} envio(s)</span>
                </td>
              </tr>
<tr v-for="item in grupo.itens" :key="item.id" :class="{ 'row-sem-anexo': !temAnexo(item) }">
                <td>
                  <span class="badge-date">
                    {{ formatarDataHora(item.created_at) }}
                  </span>
                  <div v-if="!temAnexo(item)" class="aviso-sem-anexo mt-2">
                    <i class="fas fa-exclamation-triangle"></i>
                    <small>Sem anexo</small>
                  </div>
                </td>
                <td>
                  <strong>{{ item.inscricao?.concurso?.prefeitura || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ item.inscricao?.vaga?.cargo || 'N/A' }}</small>
                </td>
                <td>
                  <div class="doc-info">
                    <i class="fas fa-file-pdf text-danger"></i>
                    <span class="doc-name">{{ item.nome }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <a
                    v-if="item.link || item.caminho_anexo"
                    :href="item.link || item.caminho_anexo"
                    target="_blank"
                    class="btn btn-sm btn-info"
                    title="Visualizar anexo"
                  >
                    <i class="fas fa-file-download"></i>
                    Ver Anexo
                  </a>
                  <div v-else class="sem-anexo-alert">
                    <i class="fas fa-exclamation-circle"></i>
                    <span>Sem anexo</span>
                  </div>
                </td>
                <td>
                  <span :class="getStatusClass(item.status)">
                    {{ getStatusText(item.status) }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="action-buttons">
                    <button
                      v-if="item.status === true"
                      @click="excluir(item)"
                      class="btn btn-sm btn-danger"
                      title="Cancelar este envio"
                    >
                      <i class="fas fa-times-circle"></i>
                      Cancelar
                    </button>
                    <span v-else class="text-muted" title="Somente envios pendentes podem ser cancelados">
                      <i class="fas fa-lock"></i>
                      Bloqueado
                    </span>
                  </div>
                </td>
              </tr>
            </template>
</tbody>
          </table>
        </div>

        <!-- Cards de Documentos - Mobile -->
        <div class="mobile-cards-envios d-md-none">
          
        <template v-for="grupo in enviosAgrupadosPorInscricao">

          <div class="mobile-group-header" :key="`grp-m-${grupo.key}`">
            <i class="fas fa-folder-open"></i>
            <strong>{{ grupo.inscricao?.concurso?.prefeitura || grupo.inscricao?.concurso?.titulo || 'Concurso' }}</strong>
            <span class="text-muted"> — {{ grupo.inscricao?.vaga?.cargo || 'Cargo' }}</span>
            <span class="text-muted" v-if="grupo.inscricao?.id"> (Inscrição #{{ grupo.inscricao.id }})</span>
            <span class="text-muted"> • {{ grupo.itens.length }} envio(s)</span>
          </div>
<div v-for="item in grupo.itens" :key="item.id" class="mobile-card-envio" :class="{ 'card-sem-anexo': !temAnexo(item) }">
            <div class="card-header-envio">
              <div class="header-left">
                <span class="badge-date">
                  {{ formatarDataHora(item.created_at) }}
                </span>
                <span :class="getStatusClass(item.status)">
                  {{ getStatusText(item.status) }}
                </span>
              </div>
              <div v-if="!temAnexo(item)" class="aviso-sem-anexo-badge">
                <i class="fas fa-exclamation-triangle"></i>
                <span>Sem anexo</span>
              </div>
            </div>
            <div class="card-body-envio">
              <div class="card-item">
                <span class="label"><i class="fas fa-building"></i> Concurso:</span>
                <span class="value">{{ item.inscricao?.concurso?.prefeitura || 'N/A' }}</span>
              </div>
              <div class="card-item">
                <span class="label"><i class="fas fa-briefcase"></i> Vaga:</span>
                <span class="value">{{ item.inscricao?.vaga?.cargo || 'N/A' }}</span>
              </div>
              <div class="card-item">
                <span class="label"><i class="fas fa-file-pdf"></i> Documento:</span>
                <span class="value">{{ item.nome }}</span>
              </div>
              <div class="card-item">
                <span class="label"><i class="fas fa-paperclip"></i> Anexo:</span>
                <div class="value">
                  <a
                    v-if="item.link || item.caminho_anexo"
                    :href="item.link || item.caminho_anexo"
                    target="_blank"
                    class="btn btn-sm btn-info"
                    title="Visualizar anexo"
                  >
                    <i class="fas fa-file-download"></i>
                    Ver Anexo
                  </a>
                  <div v-else class="sem-anexo-alert">
                    <i class="fas fa-exclamation-circle"></i>
                    <span>Sem anexo</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-footer-envio">
              <button
                v-if="item.status === true"
                @click="excluir(item)"
                class="btn btn-danger btn-block-mobile"
                title="Cancelar este envio"
              >
                <i class="fas fa-times-circle"></i>
                Cancelar Envio
              </button>
              <span v-else class="text-muted d-block text-center">
                <i class="fas fa-lock"></i>
                Bloqueado
              </span>
            </div>
          </div>
        </template>

        </div>
      </template>
      </div>

    <!-- Modal de Envio de Documentos -->
    <div v-if="showModal" class="modal-overlay" @click="fecharModal">
      <div class="modal-content large" @click.stop>
        <!-- Loading Overlay no Modal -->
        <div v-if="loadingModal" class="modal-loading-overlay">
          <div class="spinner-wrapper">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="loading-text text-light mt-3">Carregando informações...</p>
      </div>
    </div>

        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-upload"></i>
            Enviar Documento
          </h4>
          <button @click="fecharModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Mensagem de Erro -->
          <div v-if="erroEnvio" class="alert alert-danger alert-dismissible fade show" role="alert">
            <i class="fas fa-exclamation-triangle"></i>
            {{ erroEnvio }}
            <button type="button" class="btn-close-alert" @click="erroEnvio = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Informações da Inscrição -->
          <div v-if="inscricaoSelecionada" class="info-box-compact mb-3">
            <div class="info-row">
              <i class="fas fa-building text-primary"></i>
              <span><strong>Concurso:</strong> {{ inscricaoSelecionada.concurso?.prefeitura }}</span>
            </div>
            <div class="info-row">
              <i class="fas fa-briefcase text-info"></i>
              <span><strong>Vaga:</strong> {{ inscricaoSelecionada.vaga?.cargo }}</span>
            </div>
          </div>

          <!-- Formulário de Envio -->
          <form @submit.prevent="enviarArquivo">
            <!-- Seleção de Prazo -->
            <div class="form-group-compact">
              <label class="form-label-compact">
                <i class="fas fa-calendar-alt"></i>
                Prazo de Envio <span class="text-danger">*</span>
              </label>
              <FieldSelect
                :options="options"
                required
                v-model="arquivo.idTipoArquivo"
                value="value"
                placeholder="Selecione o prazo"
                :class="{ 'is-invalid': !arquivo.idTipoArquivo && tentouEnviar }"
              />
              <small v-if="!arquivo.idTipoArquivo && tentouEnviar" class="text-danger">
                Selecione um prazo de envio
              </small>
            </div>

            <!-- Detalhes do Prazo Selecionado -->
            <div v-if="selectedType && selectedType.value" class="prazo-info-card mb-3">
              <div class="prazo-info-header">
                <i class="fas fa-info-circle"></i>
                <strong>{{ selectedType.descricao || selectedType.titulo || 'Documento' }}</strong>
              </div>
              <div class="prazo-info-grid">
                <div class="prazo-info-item">
                  <i class="fas fa-calendar-alt text-success"></i>
                  <div>
                    <small class="text-muted">Período</small>
                    <div class="info-value">{{ formatarData(selectedType.data_inicio) }} até {{ formatarData(selectedType.data_fim) }}</div>
                  </div>
                </div>
                <div class="prazo-info-item" v-if="selectedType.quantidade_limite_envio || selectedType.quantidade_maxima">
                  <i class="fas fa-copy text-info"></i>
                  <div>
                    <small class="text-muted">Máximo de Envios</small>
                    <div class="info-value">{{ selectedType.quantidade_limite_envio || selectedType.quantidade_maxima }}</div>
                  </div>
                </div>
                <div class="prazo-info-item" v-if="selectedType.pontuacao">
                  <i class="fas fa-star text-warning"></i>
                  <div>
                    <small class="text-muted">Pontuação</small>
                    <div class="info-value">{{ selectedType.pontuacao }} pontos</div>
                  </div>
                </div>
                <div class="prazo-info-item" v-if="selectedType.nivel">
                  <i class="fas fa-graduation-cap text-primary"></i>
                  <div>
                    <small class="text-muted">Nível</small>
                    <div class="info-value">{{ selectedType.nivel }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upload de Arquivo -->
            <div class="form-group-compact">
              <label class="form-label-compact">
                <i class="fas fa-paperclip"></i>
                Arquivo <span class="text-danger">*</span>
              </label>
              <div class="file-upload-container" :class="{ 'is-invalid': !hasArquivoSelecionado && tentouEnviar }">
                <FieldFile
                  required
                  accept=".pdf,.jpg,.jpeg,.png,.docx,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  keyInput="arquivo"
                  v-model="file"
                  label=""
                />
              </div>
              <!-- Espaçador para garantir que o botão não seja cortado -->
              <div class="file-upload-spacer"></div>
              <small v-if="!hasArquivoSelecionado && tentouEnviar" class="text-danger">
                <i class="fas fa-exclamation-circle"></i>
                Selecione um arquivo para enviar
              </small>
              <small v-else class="file-help-text-compact">
                <i class="fas fa-info-circle"></i>
                PDF, JPG, PNG, DOCX (máx. 5MB)
              </small>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharModal" :disabled="enviando">
            <i class="fas fa-times"></i>
            Cancelar
          </button>
          <button 
            class="btn btn-primary btn-loading" 
            @click="enviarArquivo" 
            :disabled="enviando"
            :class="{ 'loading': enviando }"
          >
            <span v-if="enviando" class="spinner-container">
              <span class="spinner-border spinner-border-sm" role="status"></span>
              <span class="ml-2">Enviando...</span>
            </span>
            <span v-else>
              <i class="fas fa-paper-plane"></i>
              Enviar Documento
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import { mapGetters } from "vuex"
import FieldFile from "@/components/fields/FieldFile"
import FieldSelect from "@/components/fields/FieldSelect"

export default {
  name: 'EnviosDocumentos',
  components: {
    FieldFile,
    FieldSelect,
  },

  data() {
    return {
      list: [],
      loadingInscricoes: false,
      loadingDocumentos: false,
      loadingPrazos: false,
      loadingModal: false,
      enviando: false,
      tentouEnviar: false,
      erroEnvio: '',
      arquivo: {
        idTipoArquivo: "",
      },
      file: {},
      options: [],
      prazos: [],
      todosPrazos: [],
      inscricoes: [],
      inscricaoSelecionada: null,
      envioAberto: false,
      showModal: false,
    }
  },

  computed: {
    ...mapGetters({ profile: "profile/getProfile" }),

    inscricoesAtivas() {
      const statusPermitidos = ['A', 'D', 'I']
      return this.inscricoes.filter(inscricao => 
        statusPermitidos.includes(inscricao.status)
      )
    },

    selectedType() {
      return this.options.find(option => option.value === this.arquivo.idTipoArquivo) || {}
    },

    hasArquivoSelecionado() {
      const arquivoReal = this.file instanceof File ? this.file : (this.file?.file || null)
      return arquivoReal && arquivoReal.name
    }
  
    ,

    /**
     * Agrupa os envios por inscrição/concurso (Meus Envios).
     * - Itens dentro do grupo: mais recente primeiro
     * - Grupos: pelo envio mais recente
     */
    enviosAgrupadosPorInscricao() {
      const lista = Array.isArray(this.list) ? this.list : []
      const gruposMap = new Map()

      for (const item of lista) {
        const inscricaoId =
          item?.inscricao?.id ??
          item?.inscricao_id ??
          item?.inscricaoId ??
          'sem-inscricao'

        if (!gruposMap.has(inscricaoId)) {
          gruposMap.set(inscricaoId, {
            key: String(inscricaoId),
            inscricao: item?.inscricao || null,
            itens: []
          })
        }
        gruposMap.get(inscricaoId).itens.push(item)
      }

      for (const g of gruposMap.values()) {
        g.itens.sort((a, b) => {
          const da = new Date(a?.created_at || 0).getTime()
          const db = new Date(b?.created_at || 0).getTime()
          return db - da
        })
      }

      const grupos = Array.from(gruposMap.values())

      grupos.sort((g1, g2) => {
        const t1 = new Date(g1?.itens?.[0]?.created_at || 0).getTime()
        const t2 = new Date(g2?.itens?.[0]?.created_at || 0).getTime()
        return t2 - t1
      })

      return grupos
    }
},

  async created() {
    // console.log('EnviosDocumentos: Componente criado')
    this.loadingInscricoes = true
    
    try {
      await this.carregarInscricoes()
      // Carregar todos os prazos inicialmente (sem filtro de nível)
      await this.carregarPrazos()
      await this.carregarEnvios()
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error)
    } finally {
      this.loadingInscricoes = false
    }
  },

  methods: {
    async abrirModal(inscricao) {
      // console.log('Abrindo modal para inscrição:', inscricao)
      this.showModal = true
      this.loadingModal = true
      
      // Carregar prazos específicos para esta inscrição (filtrados por nível)
      const nivelVaga = inscricao.vaga?.nivel || null
      const concursoId = inscricao.concurso_id || inscricao.concurso?.id || null
      
      await this.carregarPrazos(concursoId, nivelVaga)
      
      setTimeout(() => {
        this.inscricaoSelecionada = inscricao
        this.arquivo.idTipoArquivo = ""
        this.file = {}
        this.tentouEnviar = false
        this.loadingModal = false
      }, 300)
    },

    fecharModal() {
      this.showModal = false
      this.loadingModal = false
      this.inscricaoSelecionada = null
      this.arquivo.idTipoArquivo = ""
      this.file = {}
      this.tentouEnviar = false
      this.enviando = false
      this.erroEnvio = ''
    },

    getClassePrazo(inscricao) {
      const { prazosAbertos } = this.filtrarPrazosPorInscricao(inscricao)

      if (prazosAbertos.length === 0) return 'badge badge-danger'
      if (prazosAbertos.length <= 2) return 'badge badge-warning'
      return 'badge badge-success'
    },

    getStatusClass(status) {
      // Status é boolean: true = Ativo, false = Excluído
      if (status === true) {
        return 'badge badge-success'  // Verde para ativo
      } else if (status === false) {
        return 'badge badge-danger'   // Vermelho para excluído
      }
      return 'badge badge-secondary'  // Cinza para desconhecido
    },

    getStatusText(status) {
    if (status === true) return 'Ativo';
    if (status === false) return 'Excluído';
    return 'Desconhecido';
  },

    async carregarInscricoes() {
      try {
        // console.log('Carregando inscrições do candidato...')
        const { data } = await axios.get('/ps/inscricao')
        this.inscricoes = data || []
        // console.log('Inscrições carregadas:', this.inscricoes.length)
      } catch (error) {
        console.error('Erro ao carregar inscrições:', error)
        this.inscricoes = []
        this.$sweet.sweetMensagem("Erro ao carregar inscrições.")
      }
    },

    async carregarPrazos(concursoId = null, nivelVaga = null) {
      try {
        // console.log('Carregando prazos de envio...', { concursoId, nivelVaga })
        this.loadingPrazos = true
        
        const params = {}
        if (concursoId) {
          params.concurso_id = concursoId
        }
        if (nivelVaga) {
          params.nivel = nivelVaga
        }
        
        const { data } = await axios.get('/ps/envio/prazos', { params })
        
        // Guardar todos os prazos quando não houver filtro por concurso/nivel
        if (!concursoId && !nivelVaga) {
          this.todosPrazos = data || []
        }
        // Filtrar prazos: mostrar apenas prazos sem nível ou com nível correspondente
        let prazosFiltrados = data || []
        if (nivelVaga) {
          prazosFiltrados = prazosFiltrados.filter(prazo => {
            // Mostrar prazos sem nível (disponíveis para todos) ou com nível correspondente
            return !prazo.nivel || prazo.nivel === '' || prazo.nivel === nivelVaga
          })
        }
        
        this.prazos = prazosFiltrados.map((prazo) => ({
          ...prazo,
          label: `${prazo.descricao || prazo.titulo || 'Documento'} - De ${this.formatarData(prazo.data_inicio)} até ${this.formatarData(prazo.data_fim)}`,
          value: prazo.id
        }))
        
        this.options = this.prazos
        this.verificarPrazoEnvio()
        
        // console.log('Prazos carregados:', this.prazos.length)
      } catch (error) {
        console.error('Erro ao carregar prazos:', error)
        this.prazos = []
        this.options = []
      } finally {
        this.loadingPrazos = false
      }
    },

    verificarPrazoEnvio() {
      try {
        const agora = new Date()
        
        // Verificar se há pelo menos um prazo aberto
        this.envioAberto = this.prazos.some(prazo => {
          const inicio = new Date(prazo.data_inicio)
          const fim = new Date(prazo.data_fim)
          return agora >= inicio && agora <= fim
        })
        
        // console.log('Verificação de prazo:', {
        //   agora: agora.toISOString(),
        //   prazosAbertos: this.prazos.filter(p => {
        //     const inicio = new Date(p.data_inicio)
        //     const fim = new Date(p.data_fim)
        //     return agora >= inicio && agora <= fim
        //   }).length,
        //   envioAberto: this.envioAberto
        // })
      } catch (error) {
        console.error('Erro ao verificar prazo de envio:', error)
        this.envioAberto = false
      }
    },

    /**
     * Retorna os prazos filtrados por concurso/nivel para uma inscrição específica
     */
    filtrarPrazosPorInscricao(inscricao) {
      if (!inscricao) {
        return { prazosFiltrados: [], prazosAbertos: [] }
      }

      const agora = new Date()
      const concursoId = inscricao.concurso_id || inscricao.concurso?.id || null
      const nivelVaga = inscricao.vaga?.nivel || null

      const base = this.todosPrazos.length ? this.todosPrazos : this.prazos

      let prazosFiltrados = base
      if (concursoId) {
        prazosFiltrados = prazosFiltrados.filter(p => p.concurso_id === concursoId)
      }

      if (nivelVaga) {
        prazosFiltrados = prazosFiltrados.filter(prazo => {
          // Mostrar prazos sem nível (disponíveis para todos) ou com nível correspondente
          return !prazo.nivel || prazo.nivel === '' || prazo.nivel === nivelVaga
        })
      }

      const prazosAbertos = prazosFiltrados.filter(prazo => {
        const inicio = new Date(prazo.data_inicio)
        const fim = new Date(prazo.data_fim)
        return agora >= inicio && agora <= fim
      })

      return { prazosFiltrados, prazosAbertos }
    },

    prazosDisponiveis(inscricao) {
      const { prazosAbertos } = this.filtrarPrazosPorInscricao(inscricao)
      return prazosAbertos.length ? `${prazosAbertos.length} prazo(s) aberto(s)` : 'Sem prazo aberto'
    },

    async carregarEnvios() {
      try {
        // console.log('Carregando documentos enviados...')
        this.loadingDocumentos = true
        const { data } = await axios.get('/ps/envio/inscricao')
        
        this.list = (data || []).map((envio) => ({
          ...envio,
          nome: envio.prazoEnvio?.titulo || envio.descricao || 'Documento',
          link: envio.caminho_anexo
        }))
        
        // console.log('Documentos carregados:', this.list.length)
      } catch (error) {
        console.error('Erro ao carregar documentos:', error)
        this.list = []
        this.$sweet.sweetMensagem("Erro ao carregar documentos enviados.")
      } finally {
        this.loadingDocumentos = false
      }
    },

    async excluir(item) {
      try {
        const { value } = await this.$sweet.sweetQuestionPersonalizado(
          "Cancelar Envio de Documento",
          '<p class="font-15">Tem certeza que deseja cancelar este envio?<br><small class="text-muted">Esta ação não pode ser desfeita.</small></p>'
        )
        
        if (!value) return

        // console.log('Cancelando envio:', item.id)
        
        await axios.delete(`/ps/envio/${item.id}`)
        
        await this.carregarEnvios()
      } catch (error) {
        console.error('Erro ao cancelar envio:', error)
        
        const errorMessage = error.response?.data?.error || error.response?.data?.message || "Erro ao cancelar envio."
        this.$sweet.sweetError(errorMessage)
      }
    },

    async finalizarEnvioTitulos() {
      try {
        const { value } = await this.$sweet.sweetQuestionPersonalizado(
          "Finalizar Envio de Documentos",
          '<p class="font-15">Ao efetivar a entrega dos documentos, você não poderá enviar novos arquivos e não poderá excluir os inseridos.</p>'
        )
        
        if (!value) return

        this.$sweet.sweetWait("Finalizando envio de documentos...")
        
        const { data } = await axios.put(
          `/formulario/${this.$city}/${this.$psCity}/area-candidato/entrega-titulos-finalizada/S`
        )
        
        localStorage.setItem(`token-${this.$city}`, data?.token)
        
        this.$sweet.sweetSuccess("Envio de documentos finalizado com sucesso!")
        
        this.$store.dispatch("profile/setProfile", {
          ...this.profile,
          entrega_titulos_finalizada: "S",
        })
        
        this.isFinish = true
      } catch (error) {
        console.error('Erro ao finalizar envio:', error)
        this.$sweet.sweetError("Erro ao finalizar envio de documentos.")
      }
    },

    async enviarArquivo() {
      this.tentouEnviar = true
      this.erroEnvio = '' // Limpar erro anterior

      // Validações
      if (!this.arquivo.idTipoArquivo) {
        this.erroEnvio = "Selecione o prazo de envio."
        return
      }

      // Verificar se file é um objeto File válido
      const arquivoReal = this.file instanceof File ? this.file : (this.file?.file || null)

      if (!arquivoReal || !arquivoReal.name) {
        this.erroEnvio = "Selecione um arquivo."
        return
      }

      if (arquivoReal.size > 5 * 1024 * 1024) {
      this.erroEnvio = "O arquivo deve ter no máximo 5MB."
        return
      }

      if (!this.inscricaoSelecionada) {
        this.erroEnvio = "Selecione uma inscrição."
        return
      }

      // Iniciar envio
      try {
        this.enviando = true

        // // console.log('Iniciando envio de documento:', {
        //   prazo_id: this.arquivo.idTipoArquivo,
        //   arquivo_nome: arquivoReal.name,
        //   arquivo_tipo: arquivoReal.type,
        //   arquivo_tamanho: arquivoReal.size,
        //   inscricao_id: this.inscricaoSelecionada.id
        // })

        // Preparar FormData
        const formData = new FormData()
        formData.append("arquivo", arquivoReal)
        formData.append("prazo_envio_id", this.arquivo.idTipoArquivo)
        formData.append("descricao", this.selectedType?.descricao || this.selectedType?.titulo || this.selectedType?.label || "Documento")

        // Enviar para a API
        await axios.post(
          `/ps/envio/${this.inscricaoSelecionada.id}/registrar`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" }
          }
        )

        // Sucesso: fechar modal e recarregar
        this.fecharModal()
        await this.carregarEnvios()
        
      } catch (error) {
        console.error('Erro ao enviar documento:', error)
        
        // Definir mensagem de erro inline
        if (error.response?.data?.error) {
          this.erroEnvio = error.response.data.error
        } else if (error.response?.data?.message) {
          this.erroEnvio = error.response.data.message
        } else if (error.response?.status === 400) {
          this.erroEnvio = "Dados inválidos. Verifique os campos."
        } else if (error.response?.status === 413) {
          this.erroEnvio = "Arquivo muito grande. Máximo: 5MB."
        } else if (error.message) {
          this.erroEnvio = error.message
        } else {
          this.erroEnvio = "Erro ao enviar documento. Tente novamente."
        }
        
      } finally {
        this.enviando = false
      }
    },

    formatarData(data) {
      if (!data) return "-";
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof data === 'string') {
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return "-";
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return "-";
      }
    },

    formatarDataHora(data) {
      if (!data) return "-";
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year} 00:00`;
        }
        
        // Tratar formato do banco: 2025-11-25 07:53:15.000
        if (typeof data === 'string') {
          // Remover o 'Z' do final se existir (formato ISO)
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart, timePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
          
          // Se tiver formato ISO com T (YYYY-MM-DDTHH:mm:ss)
          if (dateString.includes('T')) {
            const [datePart, timePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
        }
        
        // Fallback: usar Date object apenas se necessário
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return "-";
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        const hora = String(dateObj.getHours()).padStart(2, '0');
        const minuto = String(dateObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
      } catch (error) {
        return "-";
      }
    },

    temAnexo(item) {
      return !!(item.link || item.caminho_anexo);
    },
  },
}
</script>

<style lang="sass" scoped>
.area-envios
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px
    
    i
      margin-right: 10px
      color: #007bff

// Alerta Importante
.alert-importante
  display: flex
  align-items: flex-start
  gap: 15px
  padding: 20px
  background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%)
  border: 2px solid #ffc107
  border-left: 5px solid #ff9800
  border-radius: 8px
  box-shadow: 0 3px 10px rgba(255, 152, 0, 0.15)
  margin-bottom: 25px
  
  .alert-icon
    flex-shrink: 0
    width: 40px
    height: 40px
    display: flex
    align-items: center
    justify-content: center
    background-color: #ff9800
    color: white
    border-radius: 50%
    font-size: 20px
    box-shadow: 0 2px 5px rgba(255, 152, 0, 0.3)
  
  .alert-content
    flex: 1
    
    .alert-title
      font-size: 16px
      font-weight: 700
      color: #856404
      margin-bottom: 12px
      text-transform: uppercase
      letter-spacing: 0.5px
    
    .alert-text
      font-size: 14px
      line-height: 1.6
      color: #856404
      margin-bottom: 10px
      
      strong
        font-weight: 600
        color: #664d03

// Section headers
.section-header
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  padding: 10px 15px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-left: 4px solid #007bff
  border-radius: 4px
  margin-bottom: 20px
  
  i
    margin-right: 8px
    color: #007bff
  
  span
    vertical-align: middle

// Formulário de envio
.envio-form-container
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 30px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

// Form elements
.form-group
  margin-bottom: 20px
  
  .form-label
    font-weight: 600
    color: #2c3e50
    margin-bottom: 8px
    display: block
    
    i
      margin-right: 6px
      color: #007bff
    
    .text-danger
      margin-left: 2px

.form-control, .vs--single
  border-radius: 6px
  border: 1px solid #ced4da
  padding: 10px 12px
  transition: all 0.3s ease
  
  &:focus
    border-color: #007bff
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25)
  
  &.is-invalid
    border-color: #dc3545
    
    &:focus
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25)

.invalid-feedback
  display: block
  margin-top: 5px
  font-size: 13px
  color: #dc3545

.form-text
  font-size: 12px
  margin-top: 5px

.file-help-text
  font-size: 12px
  color: #6c757d
  margin-top: 8px
  display: block
  
  i
    margin-right: 5px
    color: #ffc107

// Info box
.info-box
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)
  padding: 20px
  border-radius: 8px
  border-left: 4px solid #2196f3
  margin-bottom: 20px
  
  h6
    margin: 0 0 15px 0
    font-weight: 600
    
    i
      margin-right: 8px

.info-grid
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 15px
  
  .info-item
    display: flex
    align-items: flex-start
    gap: 10px
    
    &.full-width
      grid-column: 1 / -1
    
    i
      font-size: 18px
      margin-top: 2px
      flex-shrink: 0
    
    div
      flex: 1
      overflow: hidden
      
      strong
        display: block
        margin-bottom: 4px
        font-size: 13px
        color: #2c3e50
        font-weight: 600
      
      span
        color: #495057
        font-size: 14px
        line-height: 1.5
        display: block
        word-wrap: break-word

// Alerts
.alert
  padding: 15px 20px
  border-radius: 6px
  margin-bottom: 20px
  display: flex
  align-items: center
  gap: 10px
  
  i
    font-size: 18px
  
  &.alert-danger
    background-color: #f8d7da
    border: 1px solid #f5c6cb
    color: #721c24
  
  &.alert-info
    background-color: #d1ecf1
    border: 1px solid #bee5eb
    color: #0c5460

// Loading States
.loading-container
  display: flex
  justify-content: center
  align-items: center
  padding: 60px 20px
  min-height: 200px

  .spinner-wrapper
    text-align: center

    .loading-text
      color: #6c757d
      font-size: 14px
      font-weight: 500
      margin-top: 15px

.spinner-border
  width: 3rem
  height: 3rem
  border-width: 0.3em

// Empty state
.empty-state
  text-align: center
  padding: 60px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  border: 2px dashed #dee2e6
  margin: 20px 0
  
  .empty-icon
    width: 80px
    height: 80px
    margin: 0 auto 20px
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    
    i
      font-size: 36px
      color: #fff

  .empty-title
    color: #2c3e50
    font-weight: 600
    font-size: 18px
    margin-bottom: 10px

  .empty-text
    color: #6c757d
    font-size: 15px
    margin-bottom: 8px

  .empty-hint
    color: #adb5bd
    font-size: 13px
    font-style: italic

// Tables
.table-responsive
  margin-top: 15px
  border-radius: 8px
  overflow: hidden
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

  @media (max-width: 767px)
    display: none !important

// Linha sem anexo
.row-sem-anexo
  background-color: #fff3cd !important
  border-left: 4px solid #ffc107
  
  &:hover
    background-color: #ffeaa7 !important

// Aviso sem anexo
.aviso-sem-anexo
  display: flex
  align-items: center
  gap: 5px
  color: #856404
  font-weight: 600
  font-size: 11px
  padding: 4px 8px
  background-color: #fff3cd
  border-radius: 4px
  border: 1px solid #ffc107
  
  i
    font-size: 12px

.sem-anexo-alert
  display: inline-flex
  align-items: center
  gap: 6px
  color: #dc3545
  font-weight: 600
  font-size: 12px
  padding: 6px 10px
  background-color: #f8d7da
  border-radius: 4px
  border: 1px solid #f5c6cb
  
  i
    font-size: 14px

.table
  margin-bottom: 0
  
  thead.table-dark
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
    
    th
      color: #fff
      font-weight: 600
      border: none
      padding: 12px
      font-size: 14px
      
      i
        margin-right: 5px
        opacity: 0.8
  
  tbody
    tr
      transition: all 0.3s ease
      
      &:hover
        background-color: #f8f9fa
        transform: scale(1.005)
    
    td
      padding: 12px
      vertical-align: middle
      font-size: 14px

.badge-date
  background-color: #6c757d
  color: #fff
  padding: 5px 10px
  border-radius: 12px
  font-size: 12px
  font-weight: 600
  display: inline-block

.doc-info
  display: flex
  align-items: center
  gap: 10px
  
  i
    font-size: 20px
  
  .doc-name
    font-weight: 500
    color: #2c3e50

.action-buttons
  display: flex
  gap: 8px
  justify-content: center
  flex-wrap: wrap

// Modal Loading Overlay
.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(0, 0, 0, 0.7)
  z-index: 10
  display: flex
  align-items: center
  justify-content: center
  border-radius: 10px
  
  .spinner-wrapper
    text-align: center
    
    .loading-text
      color: #fff
      font-size: 14px
      font-weight: 500

// Badge Status
.badge
  font-size: 12px
  padding: 5px 12px
  border-radius: 12px
  font-weight: 600
  display: inline-block

  &.badge-warning
    background: #ffc107
    color: #000 !important

  &.badge-success
    background: #28a745
    color: #fff !important

  &.badge-danger
    background: #dc3545
    color: #fff !important

  &.badge-secondary
    background: #6c757d
    color: #fff !important

// Override para campo de arquivo no modal - Usando :deep() e ::v-deep para penetrar no componente FieldFile
.modal-content
  ::v-deep .max-25-px,
  :deep(.max-25-px),
  ::v-deep input.max-25-px,
  :deep(input.max-25-px),
  ::v-deep .form-control.max-25-px,
  :deep(.form-control.max-25-px),
  ::v-deep input[type="file"].max-25-px,
  :deep(input[type="file"].max-25-px),
  .max-25-px,
  input.max-25-px,
  .form-control.max-25-px,
  input[type="file"].max-25-px
    max-height: none !important
    height: auto !important
    min-height: 80px !important
    padding: 15px 12px !important
    padding-left: 12px !important
    overflow: visible !important
    line-height: 1.8 !important
    
    // Botão "Escolher arquivo" com altura fixa
    &::-webkit-file-upload-button
      height: 45px !important
      min-height: 45px !important
      vertical-align: middle !important
    
    &::file-selector-button
      height: 45px !important
      min-height: 45px !important
      vertical-align: middle !important

// Modal customizado
.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  z-index: 1050
  display: flex
  align-items: center
  justify-content: center
  padding: 20px
  animation: fadeIn 0.3s ease

.modal-content
  background: white
  border-radius: 10px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3)
  max-width: 600px
  width: 90vw
  max-height: 90vh
  overflow-y: auto
  overflow-x: visible !important
  position: relative
  animation: slideInUp 0.3s ease
  
  &.large
    max-width: 800px

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px 25px
  border-bottom: 1px solid #e9ecef
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 10px 10px 0 0
  
  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50
    
    i
      margin-right: 8px
      color: #ffc107

.modal-close
  background: none
  border: none
  font-size: 20px
  color: #6c757d
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 35px
  height: 35px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease
  
  &:hover
    background: #e9ecef
    color: #dc3545

.modal-body
  padding: 25px
  max-height: calc(90vh - 180px)
  overflow-y: auto
  overflow-x: visible !important
  overflow-x: visible !important
  
  .file-upload-container
    width: 100%
    overflow: visible !important
    margin-bottom: 5px
    min-width: 0 !important
    position: relative
    
    // Garantir que o container não corte o conteúdo
    > div
      overflow: visible !important
      width: 100% !important
    
    &.is-invalid
      input[type="file"]
        border-color: #dc3545 !important
        
        &:focus
          border-color: #dc3545 !important
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important
    
    .label
      display: none !important
    
    .size-label
      font-size: 11px
      color: #dc3545
      margin-top: 5px
      display: block
      text-align: right
    
    // Sobrescrever TODOS os estilos do input file, incluindo a classe max-25-px
    input[type="file"],
    input[type="file"].form-control,
    input[type="file"].max-25-px
      width: 100% !important
      max-width: 100% !important
      min-width: 0 !important
      max-height: none !important
      min-height: 70px !important
      height: auto !important
      padding: 15px 12px !important
      padding-left: 12px !important
      padding-right: 12px !important
      border: 2px solid #ced4da !important
      border-radius: 6px !important
      font-size: 14px !important
      line-height: 1.8 !important
      background-color: #fff !important
      display: block !important
      box-sizing: border-box !important
      overflow: visible !important
      white-space: normal !important
      text-overflow: clip !important
      vertical-align: middle !important
      
      // Garantir que o botão "Escolher arquivo" não seja cortado
      &::-webkit-file-upload-button
        padding: 12px 24px !important
        margin-right: 15px !important
        margin-left: 0 !important
        border: 1px solid #ced4da !important
        border-radius: 4px !important
        background-color: #f8f9fa !important
        color: #495057 !important
        font-size: 14px !important
        font-weight: 500 !important
        cursor: pointer !important
        white-space: nowrap !important
        overflow: visible !important
        min-width: 150px !important
        max-width: none !important
        width: auto !important
        height: 45px !important
        min-height: 45px !important
        line-height: 1.5 !important
        display: inline-block !important
        flex-shrink: 0 !important
        vertical-align: middle !important
      
      // Para Firefox
      &::file-selector-button
        padding: 12px 24px !important
        margin-right: 15px !important
        margin-left: 0 !important
        border: 1px solid #ced4da !important
        border-radius: 4px !important
        background-color: #f8f9fa !important
        color: #495057 !important
        font-size: 14px !important
        font-weight: 500 !important
        cursor: pointer !important
        white-space: nowrap !important
        overflow: visible !important
        min-width: 150px !important
        max-width: none !important
        width: auto !important
        height: 45px !important
        min-height: 45px !important
        line-height: 1.5 !important
        display: inline-block !important
        flex-shrink: 0 !important
        vertical-align: middle !important
      
      &:focus
        border-color: #007bff !important
        outline: none !important
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important
      
      &:disabled
        background-color: #e9ecef !important
        cursor: not-allowed !important
    
    // Sobrescrever a classe max-25-px do componente FieldFile de forma mais agressiva
    .form-control.max-25-px,
    .max-25-px,
    input.max-25-px
      max-height: none !important
      height: auto !important
      min-height: 70px !important
      padding: 15px 12px !important
      padding-left: 12px !important
      overflow: visible !important
      line-height: 1.8 !important
  
  .instrucao-texto
    line-height: 1.8
    color: #2c3e50
    
    p
      margin-bottom: 15px
      
      &:last-child
        margin-bottom: 0
      
      strong
        color: #dc3545

.modal-footer
  display: flex
  justify-content: center
  padding: 20px 25px
  border-top: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 0 0 10px 10px

// Buttons
.btn
  border-radius: 6px
  padding: 8px 16px
  font-weight: 500
  transition: all 0.3s ease
  border: none
  
  &:hover
    transform: translateY(-2px)
    box-shadow: 0 4px 8px rgba(0,0,0,0.2)
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none
  
  i
    margin-right: 6px
  
  &.btn-sm
    padding: 6px 12px
    font-size: 13px
  
  &.btn-info
    background-color: #17a2b8
    color: #fff
    text-decoration: none
    
    &:hover
      background-color: #138496
      color: #fff
      text-decoration: none

// Animations
@keyframes fadeIn
  from
    opacity: 0
  to
    opacity: 1

@keyframes slideInUp
  from
    opacity: 0
    transform: translateY(20px) scale(0.95)
  to
    opacity: 1
    transform: translateY(0) scale(1)

// Utility classes
.weight-400
  font-weight: 400

// Responsividade
@media (max-width: 768px)
  .info-grid
    grid-template-columns: 1fr
    
    .info-item.full-width
      grid-column: 1
  
  .modal-content
    width: 95vw
  
  .table
    font-size: 12px
    
    th, td
      padding: 8px
  
  .action-buttons
    flex-direction: column
    
    .btn
      width: 100%
  
  .envio-form-container
    padding: 20px

// Estilos Compactos para Modal
.info-box-compact
  background: #f8f9fa
  padding: 12px 15px
  border-radius: 6px
  border-left: 3px solid #007bff
  
  .info-row
    display: flex
    align-items: center
    gap: 8px
    margin-bottom: 6px
    font-size: 13px
    
    &:last-child
      margin-bottom: 0
    
    i
      font-size: 14px
      flex-shrink: 0
    
    strong
      font-weight: 600

.prazo-info-card
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)
  border: 1px solid #bbdefb
  border-radius: 8px
  padding: 12px 15px
  
  .prazo-info-header
    display: flex
    align-items: center
    gap: 8px
    margin-bottom: 10px
    color: #1976d2
    font-size: 14px
    
    i
      font-size: 16px
    
    strong
      font-weight: 600
  
  .prazo-info-grid
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr))
    gap: 12px
  
  .prazo-info-item
    display: flex
    align-items: flex-start
    gap: 8px
    
    i
      font-size: 16px
      margin-top: 2px
      flex-shrink: 0
    
    div
      flex: 1
      
      small
        display: block
        font-size: 11px
        color: #6c757d
        margin-bottom: 2px
        font-weight: 500
      
      .info-value
        font-size: 13px
        color: #2c3e50
        font-weight: 600
        line-height: 1.3

.form-group-compact
  margin-bottom: 15px
  
  .form-label-compact
    font-weight: 600
    color: #2c3e50
    margin-bottom: 6px
    display: block
    font-size: 13px
    
    i
      margin-right: 5px
      color: #007bff
  
  .text-danger
    display: block
    margin-top: 4px
    font-size: 12px

.file-upload-spacer
  min-height: 5px
  width: 100%
  display: block
  clear: both

.file-help-text-compact
  display: block
  margin-top: 4px
  color: #6c757d
  font-size: 11px
  
  i
    margin-right: 4px

// Alert compacto
.alert
  padding: 10px 12px
  border-radius: 6px
  margin-bottom: 15px
  display: flex
  align-items: center
  gap: 8px
  font-size: 13px
  
  i
    font-size: 16px
  
  &.alert-danger
    background-color: #f8d7da
    border: 1px solid #f5c6cb
    color: #721c24

.btn-close-alert
  background: none
  border: none
  color: inherit
  cursor: pointer
  padding: 4px
  margin-left: auto
  opacity: 0.7
  transition: opacity 0.2s
  
  &:hover
    opacity: 1

// Botão com loading
.btn-loading
  position: relative
  min-width: 150px
  
  &.loading
    cursor: not-allowed
    opacity: 0.8
  
  .spinner-container
    display: flex
    align-items: center
    justify-content: center
    
    .spinner-border
      width: 14px
      height: 14px
      border-width: 2px
    
    .ml-2
      margin-left: 8px

// Modal footer ajustado
.modal-footer
  gap: 10px
  
  .btn
    min-width: 120px

// Mobile Cards
.mobile-cards
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

.mobile-card
  background: #fff
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  transition: all 0.3s ease
  
  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-mobile
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
  color: #fff
  padding: 15px
  display: flex
  align-items: center
  gap: 10px
  
  i
    font-size: 18px
    opacity: 0.9
  
  strong
    font-size: 14px
    font-weight: 600
    flex: 1
    line-height: 1.4

.card-body-mobile
  padding: 15px
  
  .card-item
    display: flex
    align-items: flex-start
    margin-bottom: 12px
    gap: 8px
    flex-wrap: wrap
    
    &:last-child
      margin-bottom: 0
    
    i
      color: #007bff
      font-size: 16px
      flex-shrink: 0
      min-width: 18px
      margin-top: 2px
    
    .label
      font-weight: 600
      color: #6c757d
      font-size: 13px
      flex-shrink: 0
      min-width: fit-content
    
    .value
      flex: 1
      color: #2c3e50
      font-size: 14px
      line-height: 1.4
      text-align: left
      word-break: break-word
      min-width: 0
      overflow-wrap: break-word

.card-footer-mobile
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  
  .btn-block-mobile
    width: 100%
    padding: 12px
    font-size: 14px
    font-weight: 600
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    
    i
      font-size: 16px

@media (max-width: 768px)
  .prazo-info-card
    .prazo-info-grid
      grid-template-columns: 1fr

@media (max-width: 576px)
  .modal-content
    width: 98vw
  
  .area-envios
    padding: 15px 10px
    
    h5
      font-size: 16px
  
  .alert-importante
    padding: 15px
    gap: 12px
    
    .alert-icon
      width: 35px
      height: 35px
      font-size: 18px
    
    .alert-content
      .alert-title
        font-size: 14px
        margin-bottom: 8px
      
      .alert-text
        font-size: 13px
        line-height: 1.5
  
  .section-header
    font-size: 14px
    padding: 8px 12px
  
  .prazo-info-card
    padding: 10px 12px
    
    .prazo-info-header
      font-size: 13px
    
    .prazo-info-item
      i
        font-size: 14px
      
      .info-value
        font-size: 12px

  .card-body-mobile
    padding: 12px
    
    .card-item
      gap: 6px
      margin-bottom: 10px
      
      i
        font-size: 14px
        min-width: 16px
      
      .label
        font-size: 12px
      
      .value
        font-size: 13px
        line-height: 1.5

  .card-header-mobile
    padding: 12px
    
    i
      font-size: 16px
    
    strong
      font-size: 13px

  .card-footer-mobile
    padding: 12px
    
    .btn-block-mobile
      padding: 10px
      font-size: 13px

// Mobile Cards para Envios
.mobile-cards-envios
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

  @media (min-width: 768px)
    display: none !important

.mobile-card-envio
  background: #fff
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  transition: all 0.3s ease
  
  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

  &.card-sem-anexo
    border-left: 4px solid #ffc107
    background-color: #fffbf0

.card-header-envio
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
  padding: 12px 15px
  display: flex
  justify-content: space-between
  align-items: center
  flex-wrap: wrap
  gap: 10px
  
  .header-left
    display: flex
    align-items: center
    gap: 10px
    flex-wrap: wrap

  .aviso-sem-anexo-badge
    display: flex
    align-items: center
    gap: 6px
    color: #fff
    font-weight: 600
    font-size: 11px
    padding: 4px 10px
    background-color: #ffc107
    border-radius: 12px
    border: 1px solid #ff9800
    
    i
      font-size: 12px

.card-body-envio
  padding: 15px
  
  .card-item
    display: flex
    align-items: flex-start
    margin-bottom: 12px
    gap: 8px
    flex-wrap: wrap
    
    &:last-child
      margin-bottom: 0
    
    .label
      font-weight: 600
      color: #6c757d
      font-size: 13px
      flex-shrink: 0
      min-width: fit-content
      display: flex
      align-items: center
      gap: 6px
      
      i
        color: #007bff
        font-size: 14px
        width: 16px
    
    .value
      flex: 1
      color: #2c3e50
      font-size: 14px
      line-height: 1.4
      text-align: left
      word-break: break-word
      min-width: 0
      overflow-wrap: break-word

.card-footer-envio
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  
  .btn-block-mobile
    width: 100%
    padding: 12px
    font-size: 14px
    font-weight: 600
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    
    i
      font-size: 16px

// Responsividade adicional para cards de envios
@media (max-width: 576px)
  .card-body-envio
    padding: 12px
    
    .card-item
      gap: 6px
      margin-bottom: 10px
      flex-direction: column
      align-items: flex-start
      
      .label
        width: 100%
        font-size: 12px
        
        i
          font-size: 13px
          width: 14px
      
      .value
        width: 100%
        font-size: 13px
        padding-left: 22px
        line-height: 1.5

  .card-header-envio
    padding: 10px 12px
    flex-direction: column
    align-items: flex-start
    
    .header-left
      width: 100%
      flex-direction: column
      align-items: flex-start
      gap: 8px

  .card-footer-envio
    padding: 12px
    
    .btn-block-mobile
      padding: 10px
      font-size: 13px

// Sobrescrita máxima para o campo de arquivo no modal
.modal-content .file-upload-container
  position: relative !important
  overflow: visible !important
  min-height: 80px !important
  
  // Penetrar no componente FieldFile usando :deep() e ::v-deep (Vue 2 e Vue 3)
  ::v-deep input,
  :deep(input),
  ::v-deep input[type="file"],
  :deep(input[type="file"]),
  ::v-deep .form-control,
  :deep(.form-control),
  ::v-deep .max-25-px,
  :deep(.max-25-px),
  ::v-deep input.max-25-px,
  :deep(input.max-25-px),
  ::v-deep .form-control.max-25-px,
  :deep(.form-control.max-25-px)
    max-height: none !important
    height: auto !important
    min-height: 70px !important
    padding: 15px 12px !important
    padding-left: 12px !important
    overflow: visible !important
    line-height: 1.8 !important
    box-sizing: border-box !important
    display: block !important
    width: 100% !important
    
    // Botão "Escolher arquivo" com altura adequada
    &::-webkit-file-upload-button
      padding: 12px 24px !important
      height: 45px !important
      min-height: 45px !important
      max-height: 45px !important
      line-height: 1.5 !important
      vertical-align: middle !important
      overflow: visible !important
    
    &::file-selector-button
      padding: 12px 24px !important
      height: 45px !important
      min-height: 45px !important
      max-height: 45px !important
      line-height: 1.5 !important
      vertical-align: middle !important
      overflow: visible !important

// Aplicar estilos também diretamente no .modal-content para máxima especificidade
.modal-content
  ::v-deep .file-upload-container,
  :deep(.file-upload-container)
    overflow: visible !important
    min-height: 80px !important
    position: relative !important
    
    > div
      overflow: visible !important
      min-height: 80px !important
      
      ::v-deep input,
      :deep(input),
      ::v-deep .max-25-px,
      :deep(.max-25-px)
        max-height: none !important
        min-height: 70px !important
        height: auto !important
        padding: 15px 12px !important
        overflow: visible !important
</style>
